import { orderBy } from 'lodash';
import { useCallback } from 'react';
import { useParams } from 'react-router';

import { queryClient } from '@/src/lib/react-query/react-query';

import { emailEntriesKeys } from './EmailThreadEntryViewer/api/getEmailEntries';
import { emailEntryKeys } from './EmailThreadEntryViewer/api/getEmailEntry';
import { emailThreadAttachmentsKeys } from './EmailThreadEntryViewer/api/getEmailThreadAttachments';
import { emailFileDetailsKeys } from './EmailThreadEntryViewer/components/EmailFileEntryViewer/api/getEmailFileDetails';
import { EMAIL_THREAD_PAGE_LIMIT } from './EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/EmailEntries';
import { fileDetailsKeys } from './FileEntryViewer/api/getFileDetails';
import { getStaleFileEntryFromCache } from './useEntryPanelSocketSubscriptions';
import useSyncInboxEntry from './useSyncInboxEntry';

import { useInbox } from '../../store/inbox.store';
import { useInboxEntryPanel } from '../../store/inboxEntryPanel.store';
import {
  InboxEntryStatus,
  InboxEntryType,
  View,
  RawEmailEntry,
  EmailDeliveryType,
} from '../../types';
import {
  DeletedEmailEntries,
  isRefreshedEmailEntryData,
  RefreshedEmailEntryData,
  UpdatedEmailEntries,
  UpdatedEmailFiles,
} from '../../types/SocketData';
import { isStaleEntry } from '../../types/StaleEntry';
import useInboxSearchParams from '../../useInboxSearchParams';
import { inboxSuggestionsKeys } from '../Header/FilterButtons/SearchFilter/api/getInboxSuggestions';
import { inboxEntriesKeys } from '../Sidebar/api/getInboxEntries';
import { getDestinationView } from '../Sidebar/InboxEntries/useInboxEntriesSocketSubscriptions';

function hasHighestId(array, newObj) {
  // Get the maximum id value from existing objects in the array
  const maxId = array.reduce((max, obj) => (obj.id > max ? obj.id : max), -Infinity);

  // Check if the id of the new object is greater than or equal to the maximum id
  return newObj.id >= maxId;
}

const insertToEmailEntries = ({
  projectId,
  inboxEntryId,
  view,
  rawEmailEntries,
}: {
  projectId: string;
  inboxEntryId: number;
  view: View;
  rawEmailEntries: RawEmailEntry[];
}) => {
  queryClient.setQueryData(
    [
      ...emailEntriesKeys.lists({
        projectId,
        inboxEntryId,
        trash: view === View.Trash,
      }),
      EMAIL_THREAD_PAGE_LIMIT,
    ],
    (prev: any) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        pages: prev.pages.map((page) => {
          const newEmailEntries = orderBy(
            [
              ...rawEmailEntries.map((rawEmailEntry) => {
                // @TODO, first item right now is always the highest id due to sort. We can use that if it doesn't change
                const isLatest = hasHighestId(page.data.emails, rawEmailEntry);

                return {
                  ...rawEmailEntry,
                  isLatest: isLatest,
                  isSelected: isLatest,
                };
              }),
              ...page.data.emails,
            ],
            ['id'],
            ['desc']
          );

          return {
            ...page,
            data: {
              ...page.data,
              'total-count': newEmailEntries.length,
              emails: newEmailEntries,
            },
          };
        }),
      };
    }
  );
};

export const useSyncStaleEntry = () => {
  const { socketData, updatedEmailEntries, deletedEmailEntries, clearSocket } = useInbox();
  const { setInboxEntry } = useInboxEntryPanel();
  const syncInboxEntry = useSyncInboxEntry();
  const { projectId } = useParams<{ projectId: string }>();
  const { filter, view } = useInboxSearchParams();

  const handleSyncStaleFileEntry = useCallback(
    (shouldSyncInboxEntry: boolean) => {
      if (!isStaleEntry(socketData)) {
        return;
      }
      const staleEntry = socketData;

      const { isDeleted, isRestored } = getStaleFileEntryFromCache(staleEntry);
      if (isDeleted || isRestored) {
        setInboxEntry(null);
        clearSocket();
        return;
      }

      queryClient.setQueryData(
        [...fileDetailsKeys.detail({ fileId: staleEntry.data.rawEntry.id })],
        (prev: any) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            data: staleEntry.data.rawEntry,
          };
        }
      );

      if (shouldSyncInboxEntry) {
        syncInboxEntry(staleEntry.data.rawIboxEntry);
        clearSocket();
      }
    },
    [socketData, setInboxEntry, syncInboxEntry, clearSocket]
  );

  const handleSyncDeletedEmailEntries = useCallback(
    (deletedEmailEntries: DeletedEmailEntries) => {
      if (!deletedEmailEntries) {
        return;
      }

      if (Object.keys(deletedEmailEntries).length < 1) {
        return;
      }

      let emailEntriesCount = 0;

      queryClient.setQueryData(
        [
          ...emailEntriesKeys.lists({
            projectId,
            inboxEntryId: socketData.payload['inbox-entry-source-id'],
            trash: view === View.Trash,
          }),
          EMAIL_THREAD_PAGE_LIMIT,
        ],
        (prev: any) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            pages: prev.pages.map((page) => {
              const newEmailEntries = page.data.emails.filter(
                (emailEntry) => !deletedEmailEntries[emailEntry.id]
              );

              emailEntriesCount += newEmailEntries.length;
              return {
                ...page,
                data: {
                  ...page.data,
                  'total-count': newEmailEntries.length,
                  emails: newEmailEntries.map((ee, index) => {
                    // if last item
                    if (newEmailEntries.length === index + 1) {
                      return {
                        ...ee,
                        isSelected: true,
                      };
                    }

                    return ee;
                  }),
                },
              };
            }),
          };
        }
      );

      if (emailEntriesCount <= 1) {
        setInboxEntry(null);
      }

      // No need to invalidate/check cache for permanently deleted
      if (socketData.payload.deleted) {
        return;
      }

      // Invalidate target page.
      // @TODO: Cross check via cache and manual +-
      const isRestored = view === View.Trash ? true : false;
      const destinationView = getDestinationView(!isRestored, filter);

      queryClient.invalidateQueries([
        ...inboxEntriesKeys.lists({ projectId, filter: destinationView }),
        ...inboxSuggestionsKeys.detail({ projectId }),
      ]);
    },
    [projectId, socketData, view, filter, setInboxEntry]
  );

  const handleSyncUpdatedEmailEntries = useCallback(
    (updatedEmailEntries: UpdatedEmailEntries) => {
      if (!updatedEmailEntries) {
        return;
      }

      if (Object.keys(updatedEmailEntries).length < 1) {
        return;
      }

      if (!isStaleEntry(socketData)) {
        return;
      }

      if (!isRefreshedEmailEntryData(socketData.data.rawEntry)) {
        return;
      }

      const staleEntry = socketData;
      const inboxEntryId = staleEntry.payload['inbox-entry-source-id']; // Since they share the same inboxEntryId
      const newEmails: RefreshedEmailEntryData[] = [];
      const updatedEmails: UpdatedEmailEntries = [];
      const updatedEmailFiles: UpdatedEmailFiles = [];

      const emailEntriesPages = queryClient.getQueryData([
        ...emailEntriesKeys.lists({
          projectId,
          inboxEntryId: socketData.payload['inbox-entry-source-id'],
          trash: view === View.Trash ? true : false,
        }),
        EMAIL_THREAD_PAGE_LIMIT,
      ]) as any;
      const emailEntries =
        emailEntriesPages?.pages.flatMap((page) => page.data.emails.map((r) => r)) || [];
      // const hasMoreEmails = emailEntriesPages.pages[0].data['total-count'] < emailEntries.length;

      for (const emailId of Object.keys(updatedEmailEntries)) {
        const rawExistingEmailEntry = emailEntries.find((ee) => ee.id === +emailId);

        if (rawExistingEmailEntry) {
          updatedEmails[emailId] = updatedEmailEntries[emailId];
        } else {
          newEmails.push(updatedEmailEntries[emailId]);
        }

        if (updatedEmailEntries[+emailId].rawEmailFileEntry) {
          updatedEmailFiles[updatedEmailEntries[+emailId].rawEmailFileEntry.id] =
            updatedEmailEntries[+emailId].rawEmailFileEntry;

          queryClient.setQueryData(
            [
              ...emailFileDetailsKeys.detail({
                projectId,
                emailId: +emailId,
                inboxEntryId,
                fileId: updatedEmailEntries[+emailId].rawEmailFileEntry.id,
              }),
            ],
            (prev: any) => {
              return {
                ...prev,
                data: updatedEmailEntries[+emailId].rawEmailFileEntry,
              };
            }
          );
        }
      }

      // Append to latest
      if (newEmails.length > 0) {
        insertToEmailEntries({
          projectId,
          inboxEntryId,
          view,
          rawEmailEntries: newEmails.map((ne) => ne.rawEmailEntry),
        });
      } else {
        // Email entries update
        queryClient.setQueryData(
          [
            ...emailEntriesKeys.lists({
              projectId,
              inboxEntryId,
              trash: view === View.Trash,
            }),
            EMAIL_THREAD_PAGE_LIMIT,
          ],
          (prev: any) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    emails: page.data.emails.map((emailEntry) => {
                      if (updatedEmails[emailEntry.id]) {
                        return {
                          ...emailEntry,
                          attachments: updatedEmails[emailEntry.id].rawEmailEntry.attachments,
                          // We only update open <-> close, new to open is captured on mark as read and must be retained
                          status:
                            updatedEmails[emailEntry.id].rawEmailEntry.status !==
                            InboxEntryStatus.New
                              ? updatedEmails[emailEntry.id].rawEmailEntry.status
                              : emailEntry.status,
                          read: updatedEmails[emailEntry.id].rawEmailEntry.read,
                          'allowed-status-changes':
                            updatedEmails[emailEntry.id].rawEmailEntry['allowed-status-changes'],
                          'allow-close': updatedEmails[emailEntry.id].rawEmailEntry['allow-close'],
                        };
                      }
                      return emailEntry;
                    }),
                  },
                };
              }),
            };
          }
        );
      }

      // Update each email entry detail api
      for (const emailId of Object.keys(updatedEmails)) {
        queryClient.setQueryData(
          [
            ...emailEntryKeys.detail({
              emailId: +emailId,
              inboxEntryId,
              projectId,
            }),
          ],
          (prev: any) => {
            if (!prev) {
              return prev;
            }
            return {
              ...prev,
              data: updatedEmails[+emailId].rawEmailEntry,
            };
          }
        );
      }

      const currentInboundAttachmentCount =
        queryClient.getQueryData<{ pages } | null>([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.inbound,
          }),
        ])?.pages?.[0].data['total-count'] || 0;

      const currentOutboundAttachmentCount =
        queryClient.getQueryData<{ pages } | null>([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.outbound,
          }),
        ])?.pages?.[0].data['total-count'] || 0;

      const currentAttachmentCount = currentInboundAttachmentCount + currentOutboundAttachmentCount;

      // Update attachments
      if (currentAttachmentCount !== staleEntry.data.rawIboxEntry['total-attachments-count']) {
        // @TODO: check if there's a way we can check if paginated and not and manually + -
        queryClient.invalidateQueries([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.inbound,
          }),
        ]);
        queryClient.invalidateQueries([
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.outbound,
          }),
        ]);
      } else {
        // Just check if exists in the current list and update
        queryClient.setQueryData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.inbound,
            }),
          ],
          (prev: any) => {
            if (!prev) {
              return prev;
            }
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      if (updatedEmailFiles[attachmentItem.id]) {
                        return {
                          ...attachmentItem,
                          status: updatedEmailFiles[attachmentItem.id].status,
                          read: updatedEmailFiles[attachmentItem.id].read,
                          'allowed-status-changes':
                            updatedEmailFiles[attachmentItem.id]['allowed-status-changes'],
                          'change-status': updatedEmailFiles[attachmentItem.id]['change-status'],
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );

        queryClient.setQueryData(
          [
            ...emailThreadAttachmentsKeys.lists({
              inboxEntryId,
              projectId,
              type: EmailDeliveryType.outbound,
            }),
          ],
          (prev: any) => {
            if (!prev) {
              return prev;
            }
            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    attachments: page.data.attachments.map((attachmentItem) => {
                      if (updatedEmailFiles[attachmentItem.id]) {
                        return {
                          ...attachmentItem,
                          status: updatedEmailFiles[attachmentItem.id].status,
                          read: updatedEmailFiles[attachmentItem.id].read,
                          'allowed-status-changes':
                            updatedEmailFiles[attachmentItem.id]['allowed-status-changes'],
                          'change-status': updatedEmailFiles[attachmentItem.id]['change-status'],
                        };
                      }
                      return attachmentItem;
                    }),
                  },
                };
              }),
            };
          }
        );
      }
    },
    [socketData, projectId, view]
  );

  // Single/Direct staleEntry update
  const handleSyncStaleEntryToEmailThread = useCallback(() => {
    if (!isStaleEntry(socketData)) {
      return;
    }
    const staleEntry = socketData;
    const inboxEntryId = staleEntry.data.rawIboxEntry['source-id'];
    const emailEntryData = staleEntry.data.rawEntry as RefreshedEmailEntryData;
    const emailId = emailEntryData.rawEmailEntry.id;
    const emailFileId = staleEntry.payload['inbox-email-attachment-id'];

    // Manage email entries
    const emailEntriesPages = queryClient.getQueryData([
      ...emailEntriesKeys.lists({
        projectId,
        inboxEntryId,
        trash: view === View.Trash ? true : false,
      }),
      EMAIL_THREAD_PAGE_LIMIT,
    ]) as any;

    const emailEntries =
      emailEntriesPages?.pages.flatMap((page) => page.data.emails.map((r) => r)) || [];
    const rawExistingEmailEntry = emailEntries.find((ee) => ee.id === emailId);
    // const hasMoreEmails = emailEntriesPages.pages[0].data['total-count'] < emailEntries.length;

    if (rawExistingEmailEntry) {
      const isDeleted =
        !rawExistingEmailEntry.trash && staleEntry.data.rawEntry.rawEmailEntry.trash; // moved to trash
      const isRestored =
        rawExistingEmailEntry.trash && !staleEntry.data.rawEntry.rawEmailEntry.trash;

      if (isDeleted || isRestored) {
        // if (!hasMoreEmails) {
        queryClient.setQueryData(
          [
            ...emailEntriesKeys.lists({
              projectId,
              inboxEntryId,
              trash: view === View.Trash,
            }),
            EMAIL_THREAD_PAGE_LIMIT,
          ],
          (prev: any) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              pages: prev.pages.map((page) => {
                const newEmailEntries = page.data.emails.filter(
                  (emailEntry) => emailEntry.id !== emailId
                );

                return {
                  ...page,
                  data: {
                    ...page.data,
                    'total-count': newEmailEntries.length,
                    emails: newEmailEntries.map((ee, index) => {
                      // if last item
                      if (newEmailEntries.length === index + 1) {
                        return {
                          ...ee,
                          isSelected: true,
                        };
                      }

                      return ee;
                    }),
                  },
                };
              }),
            };
          }
        );
        // } else {
        //   queryClient.invalidateQueries([
        //     ...emailEntriesKeys.lists({
        //       projectId,
        //       inboxEntryId,
        //     }),
        //   ]);
        // }

        // @TODO: manage cache check instead of invalidating emails in trash
        queryClient.invalidateQueries([
          ...emailEntriesKeys.lists({
            projectId,
            inboxEntryId,
            trash: view === View.Trash ? false : true, // Destination page type
          }),
        ]);
      } else {
        queryClient.setQueryData(
          [
            ...emailEntriesKeys.lists({
              projectId,
              inboxEntryId,
              trash: view === View.Trash,
            }),
            EMAIL_THREAD_PAGE_LIMIT,
          ],
          (prev: any) => {
            if (!prev) {
              return prev;
            }

            return {
              ...prev,
              pages: prev.pages.map((page) => {
                return {
                  ...page,
                  data: {
                    ...page.data,
                    emails: page.data.emails.map((emailEntry) => {
                      if (emailEntry.id === emailId) {
                        return {
                          ...emailEntry,
                          attachments: emailEntryData.rawEmailEntry.attachments,
                          status: emailEntryData.rawEmailEntry.status,
                          read: emailEntryData.rawEmailEntry.read,
                          'allowed-status-changes':
                            emailEntryData.rawEmailEntry['allowed-status-changes'],
                          'allow-close': emailEntryData.rawEmailEntry['allow-close'],
                        };
                      }
                      return emailEntry;
                    }),
                  },
                };
              }),
            };
          }
        );
      }
    } else {
      // Append to latest
      insertToEmailEntries({
        projectId,
        inboxEntryId,
        view,
        rawEmailEntries: [staleEntry.data.rawEntry.rawEmailEntry],
      });
    }

    queryClient.setQueryData(
      [
        ...emailEntryKeys.detail({
          emailId: emailId,
          inboxEntryId,
          projectId,
        }),
      ],
      (prev: any) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          data: emailEntryData.rawEmailEntry,
        };
      }
    );

    const currentInboundAttachmentCount =
      queryClient.getQueryData<{ pages } | null>([
        ...emailThreadAttachmentsKeys.lists({
          inboxEntryId,
          projectId,
          type: EmailDeliveryType.inbound,
        }),
      ])?.pages?.[0].data['total-count'] || 0;

    const currentOutboundAttachmentCount =
      queryClient.getQueryData<{ pages } | null>([
        ...emailThreadAttachmentsKeys.lists({
          inboxEntryId,
          projectId,
          type: EmailDeliveryType.outbound,
        }),
      ])?.pages?.[0].data['total-count'] || 0;

    const currentAttachmentCount = currentInboundAttachmentCount + currentOutboundAttachmentCount;

    if (
      currentAttachmentCount !== staleEntry.data.rawIboxEntry['total-attachments-count'] &&
      staleEntry.data.rawIboxEntry.thread.attachments.length > 0
    ) {
      queryClient.invalidateQueries([
        ...emailThreadAttachmentsKeys.lists({
          inboxEntryId,
          projectId,
          type: EmailDeliveryType.inbound,
        }),
      ]);
      queryClient.invalidateQueries([
        ...emailThreadAttachmentsKeys.lists({
          inboxEntryId,
          projectId,
          type: EmailDeliveryType.outbound,
        }),
      ]);
    } else {
      // Just check if exists in the current list and update
      queryClient.setQueryData(
        [
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.inbound,
          }),
        ],
        (prev: any) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              const attachmentShown = page.data.attachments.find(
                (a) => a.id === staleEntry.payload['inbox-email-attachment-id']
              );

              if (!attachmentShown) {
                return {
                  ...page,
                  data: {
                    ...page.data,
                  },
                };
              }

              const newEmailFileEntry = emailEntryData.rawEmailEntry.attachments.find(
                (a) => a.id === staleEntry.payload['inbox-email-attachment-id']
              );

              return {
                ...page,
                data: {
                  ...page.data,
                  attachments: page.data.attachments.map((attachmentItem) => {
                    if (attachmentItem.id === staleEntry.payload['inbox-email-attachment-id']) {
                      return {
                        ...attachmentItem,
                        status: newEmailFileEntry.status,
                        read: newEmailFileEntry.read,
                        'allowed-status-changes': newEmailFileEntry['allowed-status-changes'],
                        'change-status': newEmailFileEntry['change-status'],
                      };
                    }
                    return attachmentItem;
                  }),
                },
              };
            }),
          };
        }
      );

      queryClient.setQueryData(
        [
          ...emailThreadAttachmentsKeys.lists({
            inboxEntryId,
            projectId,
            type: EmailDeliveryType.outbound,
          }),
        ],
        (prev: any) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            pages: prev.pages.map((page) => {
              const attachmentShown = page.data.attachments.find(
                (a) => a.id === staleEntry.payload['inbox-email-attachment-id']
              );

              if (!attachmentShown) {
                return {
                  ...page,
                  data: {
                    ...page.data,
                  },
                };
              }

              const newEmailFileEntry = emailEntryData.rawEmailEntry.attachments.find(
                (a) => a.id === staleEntry.payload['inbox-email-attachment-id']
              );

              return {
                ...page,
                data: {
                  ...page.data,
                  attachments: page.data.attachments.map((attachmentItem) => {
                    if (attachmentItem.id === staleEntry.payload['inbox-email-attachment-id']) {
                      return {
                        ...attachmentItem,
                        status: newEmailFileEntry.status,
                        read: newEmailFileEntry.read,
                        'allowed-status-changes': newEmailFileEntry['allowed-status-changes'],
                        'change-status': newEmailFileEntry['change-status'],
                      };
                    }
                    return attachmentItem;
                  }),
                },
              };
            }),
          };
        }
      );
    }

    if (emailEntryData.rawEmailFileEntry) {
      // @TODO: preset emailfile
      queryClient.setQueryData(
        [
          ...emailFileDetailsKeys.detail({
            projectId,
            emailId: emailId,
            inboxEntryId,
            fileId: emailFileId,
          }),
        ],
        (prev: any) => {
          return {
            ...prev,
            data: emailEntryData.rawEmailFileEntry,
          };
        }
      );
    }
  }, [socketData, projectId, view]);

  const handleSyncStaleEmailThread = useCallback(
    (shouldSyncInboxEntry: boolean) => {
      if (!isStaleEntry(socketData)) {
        return;
      }

      // Direccty sync
      if (!updatedEmailEntries && !deletedEmailEntries) {
        handleSyncStaleEntryToEmailThread();
      } else {
        handleSyncDeletedEmailEntries(deletedEmailEntries);
        handleSyncUpdatedEmailEntries(updatedEmailEntries);
      }

      if (shouldSyncInboxEntry) {
        syncInboxEntry(socketData.data.rawIboxEntry);
        clearSocket();
      }
    },
    [
      socketData,
      syncInboxEntry,
      clearSocket,
      handleSyncDeletedEmailEntries,
      handleSyncUpdatedEmailEntries,
      handleSyncStaleEntryToEmailThread,
      updatedEmailEntries,
      deletedEmailEntries,
    ]
  );

  const handleSyncSaleEntry = useCallback(
    (
      { shouldSyncInboxEntry = true }: { shouldSyncInboxEntry?: boolean } = {
        shouldSyncInboxEntry: true,
      }
    ) => {
      if (!socketData) {
        return;
      }

      if (socketData.type === InboxEntryType.DOCUMENT) {
        handleSyncStaleFileEntry(shouldSyncInboxEntry);
      } else if (socketData.type === InboxEntryType.THREAD) {
        handleSyncStaleEmailThread(shouldSyncInboxEntry);
      }
    },
    [socketData, handleSyncStaleFileEntry, handleSyncStaleEmailThread]
  );

  return handleSyncSaleEntry;
};
